import { Avatar, Box, Typography } from '@mui/material'
import SegmentBaggageWrapper from './segmentBaggage'
import PnrBadge from '@/src/components/desktop/tripDetails/PnrBadge/PnrBadge'

interface SegmentBlockProps {
    segmentKey: string
    airlineLogo: string
    airlineName: string
    flightNumber: string
    cabinType: string
    departureTime: string
    departureDisplayDate: string
    departureCode: string
    departureAirport: string
    duration: string
    arrivalTime: string
    arrivalDisplayDate: string
    arrivalAirport: string
    arrivalCode: string
    showBaggageInfo?: boolean
    operatedBy: string
    pnr?: string | null
    pnrCompact?: boolean
}

const SegmentBlock = ({
    segmentKey,
    airlineLogo,
    airlineName,
    flightNumber,
    cabinType,
    departureTime,
    departureDisplayDate,
    departureCode,
    departureAirport,
    duration,
    arrivalTime,
    arrivalDisplayDate,
    arrivalAirport,
    arrivalCode,
    showBaggageInfo,
    operatedBy,
    pnr,
    pnrCompact
}: SegmentBlockProps) => {
    return (
        <Box>
            {/* Airline Info Row - PNR on right when provided (trip details mobile) */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: pnr ? 'space-between' : 'flex-start',
                    mb: 1.5,
                    gap: 1
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                        src={airlineLogo}
                        alt={airlineName}
                        variant="square"
                        sx={{
                            width: 32,
                            height: 32,
                            mr: 1.5,
                            '& img': { objectFit: 'contain' }
                        }}
                    />
                    <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: 14, color: 'textColors.primary' }}>
                            {airlineName} |{' '}
                            <Typography
                                component="span"
                                sx={{ fontSize: 14, fontWeight: 400, color: 'textColors.secondary' }}
                            >
                                {flightNumber}
                            </Typography>
                        </Typography>

                        {operatedBy && (
                            <Typography sx={{ fontSize: 12, fontWeight: 400, color: 'textColors.secondary' }}>
                                {operatedBy}
                            </Typography>
                        )}
                    </Box>
                </Box>
                {pnr && <PnrBadge pnr={pnr} compact={pnrCompact} />}
            </Box>

            {/* Flight Times and Duration Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                {/* Departure */}
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 24, color: 'textColors.primary' }}>
                        {departureTime}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'textColors.secondary' }}>{departureDisplayDate}</Typography>
                </Box>

                {/* Duration and Flight Path */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', mx: 2, mb: 1.5 }}>
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: 'textColors.secondary',
                            mb: 1,
                            textAlign: 'center'
                        }}
                    >
                        {duration}
                    </Typography>

                    {/* Horizontal Flight Path */}
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', position: 'relative' }}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'textColors.secondary'
                            }}
                        />
                        <Box
                            sx={{
                                flex: 1,
                                height: 2,
                                bgcolor: 'textColors.secondary'
                            }}
                        />
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'textColors.secondary'
                            }}
                        />
                    </Box>
                </Box>

                {/* Arrival */}
                <Box sx={{ flex: 1, textAlign: 'right' }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 24, color: 'textColors.primary' }}>
                        {arrivalTime}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'textColors.secondary' }}>{arrivalDisplayDate}</Typography>
                </Box>
            </Box>

            {/* Airport Details Row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 12, color: 'textColors.primary' }}>
                        {departureCode}
                    </Typography>
                    <Typography sx={{ fontSize: 10, fontWeight: 500, color: 'textColors.secondary' }}>
                        {departureAirport}
                    </Typography>
                </Box>
                <Box sx={{ flex: 1, textAlign: 'right' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 12, color: 'textColors.primary' }}>
                        {arrivalCode}
                    </Typography>
                    <Typography sx={{ fontSize: 10, fontWeight: 500, color: 'textColors.secondary' }}>
                        {arrivalAirport}
                    </Typography>
                </Box>
            </Box>

            {/* Baggage Section */}
            {!!showBaggageInfo && <SegmentBaggageWrapper segmentKey={segmentKey} />}
        </Box>
    )
}

export default SegmentBlock
